import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell, Ticket, Mail, Trash2, CheckCheck, RefreshCw, X } from "lucide-react";
import { apiUrl } from "../../../utils/config";

const TYPE_META = {
  new_ticket:    { label: "New Ticket",      icon: <Ticket size={15} className="text-blue-500" />,   bg: "bg-blue-50",   text: "text-blue-700"   },
  ticket_reply:  { label: "Ticket Reply",    icon: <Ticket size={15} className="text-orange-500" />, bg: "bg-orange-50", text: "text-orange-700" },
  ticket_status: { label: "Status Update",   icon: <Ticket size={15} className="text-green-500" />,  bg: "bg-green-50",  text: "text-green-700"  },
  new_contact:   { label: "Contact Form",    icon: <Mail   size={15} className="text-purple-500" />, bg: "bg-purple-50", text: "text-purple-700" },
};

const NAV_MAP = {
  ticket:  "/admin/tickets",
  contact: "/admin/contacts",
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "";

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread,        setUnread]        = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState("all"); // all | unread | ticket | contact
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/notifications/admin`);
      if (res.data.status) {
        setNotifications(res.data.notifications);
        setUnread(res.data.unread);
      }
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = notifications.filter(n => {
    if (filter === "unread")  return !n.is_read;
    if (filter === "ticket")  return n.entity_type === "ticket";
    if (filter === "contact") return n.entity_type === "contact";
    return true;
  });

  const markRead = async (n) => {
    if (n.is_read) return;
    await axios.put(`${apiUrl}/notifications/${n.id}/read`).catch(() => {});
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.put(`${apiUrl}/notifications/read-all`, { recipient_type: "admin" }).catch(() => {});
    setNotifications(prev => prev.map(x => ({ ...x, is_read: true })));
    setUnread(0);
  };

  const remove = async (n) => {
    await axios.delete(`${apiUrl}/notifications/${n.id}`).catch(() => {});
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    if (!n.is_read) setUnread(prev => Math.max(0, prev - 1));
    setDeleteConfirm(null);
  };

  const handleClick = async (n) => {
    await markRead(n);
    if (NAV_MAP[n.entity_type]) navigate(NAV_MAP[n.entity_type]);
  };

  const counts = {
    all:     notifications.length,
    unread:  notifications.filter(n => !n.is_read).length,
    ticket:  notifications.filter(n => n.entity_type === "ticket").length,
    contact: notifications.filter(n => n.entity_type === "contact").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Notifications</h1>
          <p className="text-sm text-gray-500 mt-0.5">{unread} unread · {notifications.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-600"
            >
              <CheckCheck size={15} /> Mark all read
            </button>
          )}
          <button onClick={load} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors" title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          ["all",     "All",           counts.all],
          ["unread",  "Unread",        counts.unread],
          ["ticket",  "Tickets",       counts.ticket],
          ["contact", "Contact Forms", counts.contact],
        ].map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-1.5 text-sm rounded-full font-semibold transition-colors ${
              filter === key ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {label} <span className="opacity-70">({count})</span>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Bell size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No notifications</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map(n => {
              const meta = TYPE_META[n.type] || { label: n.type, icon: <Bell size={15} className="text-gray-400" />, bg: "bg-gray-50", text: "text-gray-600" };
              return (
                <div
                  key={n.id}
                  className={`flex items-start gap-4 px-5 py-4 transition-colors ${!n.is_read ? "bg-orange-50/40" : "hover:bg-gray-50"}`}
                >
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${meta.bg}`}>
                    {meta.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 flex-wrap">
                      <p className={`text-sm leading-snug ${!n.is_read ? "font-bold text-gray-900" : "font-medium text-gray-700"}`}>
                        {n.title}
                      </p>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.bg} ${meta.text}`}>
                        {meta.label}
                      </span>
                      {!n.is_read && (
                        <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mt-1 flex-shrink-0" />
                      )}
                    </div>
                    {n.body && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[11px] text-gray-400 mt-1">{fmtDate(n.created_at)}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                    {NAV_MAP[n.entity_type] && (
                      <button
                        onClick={() => handleClick(n)}
                        className="px-3 py-1.5 text-xs font-bold text-orange-600 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors"
                      >
                        View
                      </button>
                    )}
                    {!n.is_read && (
                      <button
                        onClick={() => markRead(n)}
                        className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Mark as read"
                      >
                        <CheckCheck size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteConfirm(n)}
                      className="p-1.5 rounded-xl hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-80">
            <h3 className="font-extrabold text-gray-900 mb-2">Delete notification?</h3>
            <p className="text-sm text-gray-500 mb-5">This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={() => remove(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600 transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
